/**
 * MockupStep3Page — Preview-only, 0 Backend, 0 LLM.
 *
 * Zweck: UI-/UX-Vorschau der Step-3-Verbesserungen (Vordienstzeiten-Erfassung)
 * mit statischen Beispieldaten. Referenziert die Findings D1–D16 aus
 * /app/memory/UI_UX_AUDIT_2026-02.md.
 *
 * Aufruf: /mockup/step3  (public, keine Auth)
 */

import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Trash2,
  Repeat2,
  Filter,
  Clock,
  BadgeCheck,
  FileWarning,
  LineChart,
  Layers,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from "lucide-react";
import { cn } from "../lib/utils";

// -------------------------------------------------------------
// Helpers zur Daten-Herleitung
// -------------------------------------------------------------
function parseDMY(s) {
  const [d, m, y] = s.split(".").map(Number);
  return new Date(y, m - 1, d);
}

function daysBetween(from, to) {
  const a = parseDMY(from);
  const b = parseDMY(to);
  return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
}

function fmtDuration(from, to) {
  const days = daysBetween(from, to);
  const years = Math.floor(days / 365);
  const remDays = days - years * 365;
  const months = Math.floor(remDays / 30);
  const finalDays = remDays - months * 30;
  const parts = [];
  if (years) parts.push(`${years} J.`);
  if (months) parts.push(`${months} M.`);
  if (!years && !months) parts.push(`${finalDays} T.`);
  return parts.join(" ") || "1 T.";
}

function daysToYearsFloat(from, to) {
  return daysBetween(from, to) / 365;
}

// Formatiert Tage in Jahre / Monate / Tage (arbeitsrechtliche Konvention:
// 30 Tage = 1 Monat, 360 Tage = 12 Monate). Gibt Objekt zurück für flexibles Rendering.
function daysToYMD(totalDays) {
  const days = Math.max(0, Math.round(totalDays));
  const y = Math.floor(days / 360);
  const rem = days - y * 360;
  const m = Math.floor(rem / 30);
  const d = rem - m * 30;
  return { y, m, d };
}

// Kompaktes Einzeiler-Format wie im bestehenden UI: "7 J.", "4 J. 11 M. 8 T.",
// "3 M. 15 T.". Null-Bestandteile werden weggelassen (außer alles ist 0).
function fmtYMDShort(totalDays) {
  const { y, m, d } = daysToYMD(totalDays);
  const parts = [];
  if (y) parts.push(`${y} J.`);
  if (m) parts.push(`${m} M.`);
  if (d) parts.push(`${d} T.`);
  return parts.length ? parts.join(" ") : "0 T.";
}

// Berechnet das exakte Datum, an dem die kumulierte Anrechnung
// die Höchstanrechnungsdauer erreicht. Null wenn nie erreicht.
// Berücksichtigt Anrechnungsfaktor pro Position + arbeitsrechtliche
// 360-Tage-Konvention (12 * 30 = 360).
function computeCapReachedDate(positions, capYears) {
  const sorted = [...positions]
    .filter((p) => p.included)
    .sort((a, b) => parseDMY(a.from) - parseDMY(b.from));
  let cumulativeDays = 0;
  const capDays = capYears * 360;
  for (const p of sorted) {
    const posDays = daysBetween(p.from, p.to);
    const faktor = typeof p.faktor === "number" ? p.faktor : 100;
    const effDays = Math.round(posDays * (faktor / 100));
    if (effDays <= 0) continue;
    if (cumulativeDays + effDays >= capDays) {
      const effDaysNeeded = capDays - cumulativeDays;
      // Reverse-Rechnung: wie viele Kalendertage entsprechen effDaysNeeded
      // bei aktuellem Faktor?
      const calDaysNeeded = Math.round(effDaysNeeded / (faktor / 100));
      const reached = new Date(parseDMY(p.from).getTime() + calDaysNeeded * 86400000);
      const d = String(reached.getDate()).padStart(2, "0");
      const m = String(reached.getMonth() + 1).padStart(2, "0");
      const y = reached.getFullYear();
      return `${d}.${m}.${y}`;
    }
    cumulativeDays += effDays;
  }
  return null;
}

// Klassifiziert eine Rolle in KV-REGEL vs. ANNAHME + Anrechenbarkeit
function classify(role) {
  if (!role) return { kind: "ANNAHME", included: true };
  const r = role.toLowerCase();
  // Deterministisch NICHT anrechenbar (SV-Bezug-Codes)
  if (
    r.includes("arbeitslosengeldbezug") ||
    r.includes("krankengeldbezug") ||
    r.includes("notstandshilfe") ||
    r.includes("überbrückungshilfe") ||
    r.includes("urlaubsersatzleistung")
  ) {
    return { kind: "KV-REGEL", included: false };
  }
  // Geringfügig → ANNAHME (Prüfen ob >7h/Woche = anrechenbar)
  if (r.includes("geringfügig") || r.includes("vollvers. und geringf")) {
    return { kind: "ANNAHME", included: true };
  }
  // Reguläre Beschäftigungen → KV-Regel, anrechenbar
  if (
    r.includes("angestellte") ||
    r.includes("arbeiterin") ||
    r.includes("angestelltenlehrling") ||
    r.includes("lehrling")
  ) {
    return { kind: "KV-REGEL", included: true };
  }
  return { kind: "ANNAHME", included: true };
}

function buildPositions(raw) {
  return raw.map((p, i) => {
    const c = classify(p.role);
    return {
      id: i + 1,
      from: p.from,
      to: p.to,
      duration: fmtDuration(p.from, p.to),
      employer: p.employer || "—",
      role: p.role,
      faktor: c.included ? 100 : 0,
      kind: c.kind,
      included: c.included,
      sameEmployer: false,
      overlap: p.overlap || null,
      note: c.included
        ? c.kind === "ANNAHME"
          ? "Aus SV-Auszug abgeleitet — Anstellungsart / Stundenausmaß prüfen."
          : "Reguläre KV-anrechenbare Beschäftigung."
        : "Bezugszeitraum — laut KV-Regel nicht anrechenbar.",
      tagAssumed: false,
    };
  });
}

// -------------------------------------------------------------
// FALL 1 — Semiconductor Engineer (Original-Screenshot, KV Gewerbe VG III)
// -------------------------------------------------------------
const FALL_1_RAW = [
  { from: "01.01.1991", to: "31.12.1991", employer: "Institute for Marketing and Consumer Research", role: "Technical Assistant (aus Lebenslauf)" },
  { from: "01.01.1992", to: "31.01.1996", employer: "Institute of Applied Mathematics, Graz", role: "Research Assistant (aus Lebenslauf)" },
  { from: "01.01.1996", to: "31.12.1997", employer: "Red Cross Austria", role: "Präsenzdienst (Civil Service)" },
  { from: "01.01.1997", to: "31.12.2002", employer: "austriamicrosystems AG", role: "Analog Circuit Simulator (aus Lebenslauf)" },
  { from: "01.01.2003", to: "31.12.2005", employer: "Infineon Technologies Austria AG", role: "Design Engineer (aus Lebenslauf)" },
  { from: "01.01.2006", to: "31.12.2010", employer: "NXP Semiconductors", role: "Senior Analog Designer (aus Lebenslauf)" },
  { from: "01.01.2011", to: "31.12.2014", employer: "AMS AG", role: "Lead Circuit Architect (aus Lebenslauf)" },
  { from: "01.01.2015", to: "31.12.2018", employer: "AMS AG", role: "Functional Safety Engineer (aus Lebenslauf)" },
  { from: "01.01.2019", to: "31.12.2022", employer: "AMS-OSRAM", role: "Functional Safety Team Manager (aus Lebenslauf)" },
  { from: "01.01.2023", to: "30.06.2026", employer: "AMS-OSRAM", role: "Head of Functional Safety (aus Lebenslauf)" },
];

// FALL 1 hat eine Sonderklassifizierung (alles CV = ANNAHME) — override
const FALL_1 = FALL_1_RAW.map((p, i) => {
  const isPraesenz = /präsenz|civil service/i.test(p.role);
  return {
    id: i + 1,
    from: p.from,
    to: p.to,
    duration: fmtDuration(p.from, p.to),
    employer: p.employer,
    role: p.role,
    faktor: 100,
    kind: isPraesenz ? "KV-REGEL" : "ANNAHME",
    included: true,
    sameEmployer: false,
    overlap: isPraesenz ? { days: 31, with: "Institute of Applied Mathematics, Graz" } : null,
    note: isPraesenz
      ? "Präsenzdienst (Zivildienst) — laut KV-Regelwerk voll anrechenbar. Hinweis: 31 Tag(e) überlappen mit anderer anrechenbarer Position."
      : "Aus dem Lebenslauf-Kontext abgeleitet — bitte Anstellungstyp prüfen.",
    tagAssumed: true,
  };
});

// -------------------------------------------------------------
// FALL 2 — Jacqui (SV-Auszug, Handel Arbeiter:innen, Ziel VG III)
// 57 Positionen — Extrem-Beispiel für viele Bezugszeiträume
// -------------------------------------------------------------
const FALL_2 = buildPositions([
  { from: "29.09.2016", to: "18.09.2017", employer: "KEREP Gastronomiebetrieb GmbH", role: "Arbeiterin" },
  { from: "20.09.2017", to: "30.09.2017", employer: "KEREP Gastronomiebetrieb GmbH", role: "geringfügig beschäftigte Arbeiterin" },
  { from: "01.10.2017", to: "30.11.2017", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "01.12.2017", to: "03.02.2018", employer: "DANILOVIC Srdjan KG", role: "geringfügig beschäftigte Arbeiterin" },
  { from: "05.02.2018", to: "02.04.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "04.04.2018", to: "26.04.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "28.04.2018", to: "01.05.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "05.05.2018", to: "06.05.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "10.05.2018", to: "10.05.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "12.05.2018", to: "13.05.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "14.05.2018", to: "14.05.2018", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "16.05.2018", to: "19.05.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "20.05.2018", to: "25.05.2018", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "26.05.2018", to: "03.09.2018", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "01.07.2018", to: "11.09.2018", employer: "DANILOVIC Srdjan KG", role: "geringfügig beschäftigte Arbeiterin", overlap: { days: 65, with: "Arbeitslosengeldbezug (parallel)" } },
  { from: "04.09.2018", to: "18.09.2018", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "19.09.2018", to: "26.04.2019", employer: "ISS Facility Services GmbH", role: "Arbeiterin" },
  { from: "27.04.2019", to: "27.04.2019", employer: "ISS Facility Services GmbH", role: "Urlaubsersatzleistung" },
  { from: "02.05.2019", to: "03.07.2019", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "04.07.2019", to: "19.07.2019", employer: "ISS Facility Services GmbH", role: "Arbeiterin" },
  { from: "20.07.2019", to: "20.07.2019", employer: "ISS Facility Services GmbH", role: "Urlaubsersatzleistung" },
  { from: "21.07.2019", to: "21.08.2019", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "22.08.2019", to: "25.06.2020", employer: "ISS Facility Services GmbH", role: "Arbeiterin" },
  { from: "03.07.2020", to: "14.10.2020", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "15.10.2020", to: "20.10.2020", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "21.10.2020", to: "25.11.2020", employer: "AMS Bezug", role: "Arbeitslosengeldbezug" },
  { from: "26.11.2020", to: "28.01.2021", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "29.01.2021", to: "02.03.2021", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "03.03.2021", to: "04.04.2021", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "15.04.2021", to: "02.06.2021", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "03.06.2021", to: "02.09.2021", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "03.09.2021", to: "17.09.2021", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "20.09.2021", to: "06.01.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "08.01.2022", to: "01.04.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "04.04.2022", to: "22.04.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "26.04.2022", to: "26.05.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "30.05.2022", to: "30.07.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "16.08.2022", to: "18.08.2022", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "19.08.2022", to: "31.01.2023", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "03.02.2023", to: "05.02.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "09.02.2023", to: "14.03.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "21.03.2023", to: "03.05.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "08.05.2023", to: "03.06.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "06.06.2023", to: "28.07.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "12.08.2023", to: "22.08.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "23.08.2023", to: "02.10.2023", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "03.10.2023", to: "21.10.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "01.11.2023", to: "10.11.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "14.11.2023", to: "14.11.2023", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "27.12.2023", to: "14.02.2024", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "15.02.2024", to: "16.02.2024", employer: "ÖGK Bezug", role: "Krankengeldbezug, Sonderfall" },
  { from: "17.02.2024", to: "02.04.2024", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "04.04.2024", to: "09.04.2024", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "11.04.2024", to: "17.04.2024", employer: "AMS Bezug", role: "Notstandshilfe, Überbrückungshilfe" },
  { from: "18.04.2024", to: "31.12.2024", employer: "PS3 Personalservice GmbH", role: "Arbeiterin" },
  { from: "01.01.2025", to: "08.01.2025", employer: "PS3 Personalservice GmbH", role: "Urlaubsersatzleistung" },
  { from: "01.01.2025", to: "15.05.2026", employer: "Peek & Cloppenburg B.V. & Co. KG", role: "Arbeiterin", overlap: { days: 8, with: "PS3 Personalservice — Urlaubsersatzleistung" } },
]);

// -------------------------------------------------------------
// FALL 3 — Martina (SV-Auszug, Handel Angestellte, Ziel VG III)
// 27 Positionen — Sehr fragmentierte Handels-Karriere
// -------------------------------------------------------------
const FALL_3 = buildPositions([
  { from: "28.09.2009", to: "07.07.2010", employer: "IBIS ACAM BILDUNGS GMBH", role: "Angestelltenlehrling" },
  { from: "08.11.2010", to: "01.12.2010", employer: "Aron SACHAROV KG", role: "geringfügig beschäftigte Angestellte" },
  { from: "07.12.2010", to: "13.12.2010", employer: "Jaqueline Horvath", role: "Angestellte" },
  { from: "25.01.2011", to: "30.04.2011", employer: "COHINOV KG", role: "geringfügig beschäftigte Arbeiterin" },
  { from: "06.09.2011", to: "30.09.2011", employer: "Kein Dienstgeber angegeben", role: "Angestellte" },
  { from: "01.10.2011", to: "29.10.2011", employer: "SPORT & MODE OUTLET GMBH", role: "geringfügig beschäftigte Angestellte" },
  { from: "07.02.2012", to: "16.08.2012", employer: "IBIS ACAM BILDUNGS GMBH", role: "Angestelltenlehrling" },
  { from: "28.01.2013", to: "31.05.2013", employer: "MAGDALENA KLOUCEK KG", role: "Angestellte" },
  { from: "01.06.2013", to: "26.07.2013", employer: "WILHELM KLOUCEK KG", role: "Angestellte" },
  { from: "30.10.2014", to: "31.10.2014", employer: "RGIS Austria GmbH", role: "Angestellte" },
  { from: "02.01.2015", to: "02.01.2015", employer: "Sport & Mode Outlet GmbH", role: "Angestellte" },
  { from: "10.10.2015", to: "11.10.2015", employer: "ai itali moda gmbh", role: "Angestellte" },
  { from: "17.10.2016", to: "30.06.2017", employer: "Schehrazat Collection e.U.", role: "Angestellte" },
  { from: "01.07.2017", to: "15.07.2017", employer: "Schehrazat Collection e.U.", role: "Urlaubsersatzleistung" },
  { from: "01.09.2017", to: "05.09.2017", employer: "TJX Oesterreich Ltd. & Co. KG", role: "Angestellte" },
  { from: "12.10.2017", to: "09.05.2018", employer: "New Yorker Austria KG", role: "Angestellte" },
  { from: "20.03.2019", to: "20.03.2019", employer: "TIGER STORES AUSTRIA GMBH", role: "Angestellte" },
  { from: "15.06.2019", to: "31.07.2020", employer: "JD Sports Fashion AT GmbH", role: "Angestellte" },
  { from: "01.08.2020", to: "09.08.2020", employer: "JD Sports Fashion AT GmbH", role: "Urlaubsersatzleistung" },
  { from: "10.08.2020", to: "30.09.2020", employer: "VIANELLO GmbH", role: "geringfügig beschäftigte Angestellte" },
  { from: "01.10.2020", to: "30.04.2023", employer: "JD Sports Fashion AT GmbH", role: "Angestellte" },
  { from: "03.06.2024", to: "04.06.2024", employer: "HautSinn Natural Cosmetics GmbH", role: "Angestellte" },
  { from: "03.06.2024", to: "04.06.2024", employer: "Leonora Emrulai", role: "Vollvers. und geringf. Besch. - Ang.", overlap: { days: 2, with: "HautSinn Natural Cosmetics GmbH" } },
  { from: "02.12.2024", to: "10.12.2024", employer: "MANGO (Österreich) Handels GmbH", role: "Angestellte" },
  { from: "01.01.2025", to: "13.01.2025", employer: "Deichmann GmbH", role: "Angestellte" },
  { from: "14.01.2025", to: "14.01.2025", employer: "Deichmann GmbH", role: "Urlaubsersatzleistung" },
  { from: "12.02.2025", to: "31.10.2025", employer: "WENNA GmbH", role: "Angestellte" },
]);

// -------------------------------------------------------------
// Fall-Metadaten (KV, Cap, Stichtag, Warnungen)
// -------------------------------------------------------------
const CASES = {
  fall1: {
    key: "fall1",
    label: "Fall 1 · Semiconductor (Lebenslauf)",
    shortLabel: "Fall 1",
    subtitle: "KV Gewerbe · VG III · Cap 12 J.",
    kvName: "KV Gewerbe (Angestellte)",
    kvShort: "GEWERBE",
    stichtag: "01.07.2026",
    zielVG: "Gruppe III (Hebamme)",
    cap: 12,
    positions: FALL_1,
    warnings: {
      title: "Gleichwertigkeitsprüfung erforderlich",
      summary:
        "Alle 10 extrahierten Positionen sind branchen-/fachfremd zur Ziel-VG Hebamme (Halbleiter/Elektronik vs. Gesundheitswesen). Zusätzlich fehlten in allen Zeilen die genauen Tages-Angaben — Jahresanfang / -ende automatisch angenommen.",
      bullets: [
        "Alle extrahierten Vortätigkeiten sind branchen-/fachfremd zur Ziel-Verwendungsgruppe Hebamme (Halbleiter-/Elektronikbranche vs. Gesundheitswesen) — Gleichwertigkeitsprüfung durch Sachbearbeiter erforderlich.",
        "Bei Positionen 1991–1996 (universitäre Assistenzen) war im Dokument kein Tag angegeben — Jahreserster/Jahresende angenommen.",
        "Bei allen weiteren Positionen war im Dokument kein Tag angegeben — Jahreserster/Jahresende angenommen.",
      ],
    },
  },
  fall2: {
    key: "fall2",
    label: "Fall 2 · Jacqui (SV-Auszug)",
    shortLabel: "Fall 2",
    subtitle: "KV Handel Arbeiter:innen · Cap 7 J.",
    kvName: "KV Handel (Handelsarbeiter:innen)",
    kvShort: "HANDEL ARBEITER",
    stichtag: "16.05.2026",
    zielVG: "Lohngruppe C",
    cap: 7,
    positions: FALL_2,
    warnings: {
      title: "Prüfung erforderlich — 57 Positionen aus SV-Auszug",
      summary:
        "SV-Auszug enthält viele Bezugszeiträume (Arbeitslosengeld, Notstandshilfe, Krankengeld) — diese sind laut KV nicht anrechenbar. Nur reguläre Beschäftigungen zählen. Zwei Positionen überlappen (parallel geringfügig + AMS-Bezug).",
      bullets: [
        "45 Bezugszeiträume (AMS/ÖGK) automatisch als nicht anrechenbar markiert (KV-Regel deterministisch).",
        "5 geringfügig beschäftigte Positionen als ANNAHME markiert — bitte Stundenausmaß (>7 h/Woche?) prüfen.",
        "2 Positionen überlappen zeitlich mit einer parallelen Bezugsposition (siehe rot markierte Zeilen).",
        "1 Position (KEREP Gastronomiebetrieb 2016/17) branchen-/fachfremd zum Handel — Gleichwertigkeitsprüfung durch Sachbearbeiter.",
      ],
    },
  },
  fall3: {
    key: "fall3",
    label: "Fall 3 · Martina (SV-Auszug)",
    shortLabel: "Fall 3",
    subtitle: "KV Handel Angestellte · Cap 7 J.",
    kvName: "KV Handel (Angestellte und Lehrlinge in Handelsbetrieben)",
    kvShort: "HANDEL ANGESTELLTE",
    stichtag: "01.11.2025",
    zielVG: "Beschäftigungsgruppe 3",
    cap: 7,
    positions: FALL_3,
    warnings: {
      title: "Sehr fragmentierte Handels-Karriere — 27 Positionen",
      summary:
        "SV-Auszug zeigt viele kurze Beschäftigungen im Handel (Ziel-VG passend). 4 geringfügige Positionen erfordern Prüfung des Stundenausmaßes. Zwei Positionen überschneiden sich am 03./04.06.2024.",
      bullets: [
        "22 reguläre Handels-Positionen als voll anrechenbar markiert (KV-Regel).",
        "4 geringfügig beschäftigte Positionen als ANNAHME markiert — Stundenausmaß prüfen.",
        "2 Angestelltenlehrling-Positionen (2009–2012) — anrechenbar nach KV, mit Faktor 100 %.",
        "3 Urlaubsersatzleistungs-Positionen automatisch als nicht anrechenbar (bereits in Vor-Position enthalten).",
      ],
    },
  },
};

// -------------------------------------------------------------
// Kleine UI-Helpers
// -------------------------------------------------------------
const Chip = ({ tone = "neutral", children, className }) => {
  const tones = {
    neutral: "bg-ink-100 text-ink-700 border-ink-200",
    brand: "bg-brand-50 text-brand-700 border-brand-200",
    warn: "bg-amber-50 text-amber-800 border-amber-300",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide",
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
};

const AuditTag = ({ ids }) => (
  <span
    className="ml-2 inline-flex items-center rounded-sm border border-brand-300 bg-brand-50 px-1.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wide text-brand-700"
    title="Referenz zum UI/UX-Audit"
  >
    {ids}
  </span>
);

const Switch = ({ checked, onChange, labelOn = "Anrechnen", labelOff = "Nicht anrechnen" }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={cn(
      "inline-flex items-center gap-2 rounded-sm border px-2 py-1 text-xs font-medium transition-crisp",
      checked
        ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
        : "border-ink-200 bg-white text-ink-500 hover:bg-ink-100"
    )}
    aria-pressed={checked}
  >
    <span
      className={cn(
        "relative inline-block h-4 w-8 shrink-0 rounded-full transition-colors",
        checked ? "bg-emerald-500" : "bg-ink-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-all",
          checked ? "left-[17px]" : "left-[2px]"
        )}
      />
    </span>
    <span className="leading-none">{checked ? labelOn : labelOff}</span>
  </button>
);

// -------------------------------------------------------------
// Timeline (D11) — v3: gestapelte Lanes, ruhige Grenz-Linie, PV-Sprache
// -------------------------------------------------------------
const Timeline = ({ positions, capReachedDate }) => {
  if (!positions.length) return null;
  // Auto-Range
  const yearsAll = positions.flatMap((p) => [
    parseInt(p.from.slice(6), 10),
    parseInt(p.to.slice(6), 10),
  ]);
  const yStart = Math.min(...yearsAll);
  const yEnd = Math.max(...yearsAll);
  const span = Math.max(1, yEnd - yStart);
  const tickCount = Math.min(8, span + 1);
  const yStartMs = new Date(yStart, 0, 1).getTime();
  const yEndMs = new Date(yEnd, 11, 31).getTime();
  const totalMs = Math.max(1, yEndMs - yStartMs);

  // Lane-Assignment: gestapelte Balken bei zeitlicher Überlappung
  const sorted = [...positions].sort((a, b) => parseDMY(a.from) - parseDMY(b.from));
  const lanes = []; // lanes[i] = to-Datum der letzten Position in Lane i
  const positionLane = new Map();
  for (const p of sorted) {
    const from = parseDMY(p.from);
    const to = parseDMY(p.to);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (lanes[i] < from.getTime()) {
        lanes[i] = to.getTime();
        positionLane.set(p.id, i);
        placed = true;
        break;
      }
    }
    if (!placed) {
      lanes.push(to.getTime());
      positionLane.set(p.id, lanes.length - 1);
    }
  }
  const laneCount = Math.max(1, lanes.length);
  const laneH = 10;
  const laneGap = 3;
  const chartH = laneCount * (laneH + laneGap) + 8;

  const capMs = capReachedDate ? parseDMY(capReachedDate).getTime() : null;
  const capLeft = capMs != null ? ((capMs - yStartMs) / totalMs) * 100 : null;

  return (
    <div className="rounded-sm border border-ink-200 bg-white p-4 pb-14">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
          <LineChart className="h-3.5 w-3.5 text-brand-500" />
          Zeitleiste ({yStart} – {yEnd})
          <AuditTag ids="D11" />
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-600">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-emerald-500" /> KV-Regel
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-[repeating-linear-gradient(45deg,theme(colors.brand.300),theme(colors.brand.300)_2px,theme(colors.brand.200)_2px,theme(colors.brand.200)_4px)] border border-brand-500" />
            Annahme
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-3 rounded-[1px] bg-ink-300" /> nicht anrechenbar
          </span>
          {capReachedDate && (
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-3 rounded-[1px] bg-emerald-500 opacity-30" /> nach Höchstanrechnungsdauer
            </span>
          )}
        </div>
      </div>
      <div className="relative rounded-sm bg-ink-50/70" style={{ height: `${chartH}px` }}>
        {/* Grid */}
        {Array.from({ length: tickCount }).map((_, i) => {
          const y = yStart + Math.round((i * span) / (tickCount - 1));
          return (
            <div key={i} className="absolute top-0 h-full" style={{ left: `${(i * 100) / (tickCount - 1)}%` }}>
              <div className="h-full w-px bg-ink-200/70" />
              <span className="absolute -bottom-4 -translate-x-1/2 text-[10px] text-ink-500">
                {y}
              </span>
            </div>
          );
        })}

        {/* Grenz-Linie: Höchstanrechnungsdauer erreicht (dezent, ink-blau).
            Linie reicht durch die Jahresachse bis zum Label unter der Zeitleiste,
            damit die Zuordnung Linie ↔ Datum-Badge sofort erkennbar ist. */}
        {capLeft != null && (
          <>
            <div
              className="pointer-events-none absolute top-0 z-10"
              style={{ left: `${capLeft}%`, height: `${chartH + 44}px` }}
            >
              <div className="h-full w-px border-l border-dashed border-sky-500/70" />
            </div>
            <div
              className={cn(
                "pointer-events-none absolute z-10 whitespace-nowrap rounded-sm border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-800",
                capLeft > 55 ? "-translate-x-full" : "translate-x-1"
              )}
              style={{ left: `${capLeft}%`, bottom: "-2.75rem" }}
            >
              Höchstanrechnungsdauer erreicht · {capReachedDate}
            </div>
          </>
        )}

        {/* Position-Balken */}
        {sorted.map((p) => {
          const from = parseDMY(p.from);
          const to = parseDMY(p.to);
          const left = ((from.getTime() - yStartMs) / totalMs) * 100;
          const width = Math.max(0.25, ((to.getTime() - from.getTime()) / totalMs) * 100);
          const isKv = p.kind === "KV-REGEL";
          const isExcluded = !p.included;
          const lane = positionLane.get(p.id) || 0;

          // Ist diese Position komplett nach der Höchstanrechnungsdauer?
          const isAfterCap =
            capMs != null && p.included && from.getTime() > capMs;
          // Oder überschreitet sie die Grenze teilweise?
          const isCrossingCap =
            capMs != null &&
            p.included &&
            from.getTime() <= capMs &&
            to.getTime() > capMs;

          const barColor = isExcluded
            ? "bg-ink-300 border-ink-400"
            : isKv
            ? "bg-emerald-500 border-emerald-600"
            : "bg-brand-300 border-brand-500";
          const pattern =
            !isKv && !isExcluded
              ? "bg-[repeating-linear-gradient(45deg,theme(colors.brand.300),theme(colors.brand.300)_4px,theme(colors.brand.200)_4px,theme(colors.brand.200)_8px)]"
              : "";
          return (
            <div
              key={p.id}
              className={cn(
                "absolute rounded-[2px] border",
                barColor,
                pattern,
                isAfterCap && "opacity-25"
              )}
              style={{
                left: `${left}%`,
                width: `${width}%`,
                top: `${6 + lane * (laneH + laneGap)}px`,
                height: `${laneH}px`,
              }}
              title={`${p.role} @ ${p.employer} (${p.from} – ${p.to})${
                isAfterCap ? " · nach Höchstanrechnungsdauer" : ""
              }${isCrossingCap ? " · reicht über die Höchstanrechnungsdauer" : ""}`}
            >
              {/* Bei Kreuzung: rechte Hälfte gedämpft */}
              {isCrossingCap && (
                <div
                  className={cn("absolute inset-y-0 rounded-r-[2px]", barColor, pattern, "opacity-25")}
                  style={{
                    left: `${((capMs - from.getTime()) / (to.getTime() - from.getTime())) * 100}%`,
                    right: 0,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// -------------------------------------------------------------
// KOMPAKT-ZEILE (Neuer Vorschlag) — v3 ruhig, keine Cap-Signale in der Zeile
// -------------------------------------------------------------
const CompactRow = ({ p, expanded, onToggle, checked, onCheck, included, onIncluded, onFaktor }) => {
  const isKv = p.kind === "KV-REGEL";
  const faktor = typeof p.faktor === "number" ? p.faktor : 100;
  const faktorManual = included && faktor !== 100;
  return (
    <div
      className={cn(
        "group rounded-sm border border-ink-200 bg-white transition-crisp",
        !included && "bg-ink-50/50 opacity-70"
      )}
    >
      {/* Kompakt-Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onCheck(e.target.checked)}
          className="h-4 w-4 rounded border-ink-300 accent-brand-500"
          aria-label={`Position ${p.id} auswählen`}
        />
        <span className="w-40 shrink-0 font-mono text-xs text-ink-700">
          {p.from} – {p.to}
        </span>
        <div className="w-28 shrink-0 leading-tight">
          <div className="text-xs font-semibold text-ink-900">
            {p.included ? fmtYMDShort(p.cappedDays || 0) : "—"}
          </div>
          <div className="text-[10px] text-ink-500">
            brutto {fmtYMDShort(daysBetween(p.from, p.to))}
          </div>
        </div>
        <span className="min-w-0 flex-1 truncate text-xs text-ink-700">
          <span className="font-medium text-ink-900">{p.role}</span>
          <span className="text-ink-500"> — {p.employer}</span>
        </span>
        {/* Anrechnungsfaktor — Inline-Feld (Variante B, analog Live-Tool) */}
        <div
          className={cn(
            "flex shrink-0 items-center gap-0.5 rounded-sm border px-1 py-0.5",
            included
              ? faktorManual
                ? "border-amber-300 bg-amber-50"
                : "border-ink-200 bg-white"
              : "border-transparent bg-transparent"
          )}
          title={
            included
              ? "Anrechnungsfaktor in % (0–100). Standard 100 %, z.B. 50 % für hälftige Anrechnung."
              : "Nicht anrechenbar"
          }
        >
          {included ? (
            <>
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                value={faktor}
                onChange={(e) => {
                  const raw = e.target.value;
                  const pct = raw === "" ? 0 : Number(raw);
                  onFaktor(pct);
                }}
                aria-label="Anrechnungsfaktor in Prozent"
                className={cn(
                  "w-10 rounded-sm bg-transparent px-0.5 py-0.5 text-right text-xs tabular-nums focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400",
                  faktorManual ? "text-amber-900 font-semibold" : "text-ink-800"
                )}
              />
              <span className={cn("text-[10px] select-none", faktorManual ? "text-amber-700" : "text-ink-500")}>%</span>
            </>
          ) : (
            <span className="text-xs text-ink-400 px-1">—</span>
          )}
        </div>
        <Chip tone={isKv ? "ok" : "warn"} className="shrink-0">
          {isKv ? (
            <>
              <BadgeCheck className="h-3 w-3" /> KV-Regel
            </>
          ) : (
            <>
              <Info className="h-3 w-3" /> Annahme
            </>
          )}
        </Chip>
        {p.sameEmployer && (
          <Chip tone="brand" className="shrink-0">
            <Repeat2 className="h-3 w-3" /> Selber AG
          </Chip>
        )}
        <div className="shrink-0">
          <Switch checked={included} onChange={onIncluded} />
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="rounded-sm p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          aria-label={expanded ? "Details schließen" : "Details öffnen"}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button
          type="button"
          className="rounded-sm p-1 text-ink-400 hover:bg-rose-50 hover:text-rose-600"
          aria-label="Position löschen"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Detail-Drawer (nur bei Klick sichtbar) */}
      {expanded && (
        <div className="grid grid-cols-1 gap-4 border-t border-ink-100 bg-ink-50/40 p-4 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                Anrechnungsfaktor
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  defaultValue={p.faktor}
                  className="w-20 rounded-sm border border-ink-200 px-2 py-1 text-sm"
                />
                <span className="text-sm text-ink-600">%</span>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-ink-700">
                <input
                  type="checkbox"
                  defaultChecked={p.sameEmployer}
                  className="h-4 w-4 rounded border-ink-300 accent-brand-500"
                />
                <Repeat2 className="h-3.5 w-3.5 text-brand-500" />
                <span className="font-medium">Selber Arbeitgeber</span>
                <span className="text-ink-500">
                  — zählt zusätzlich zur Höchstanrechnungsdauer (KV-Sonderregel).
                </span>
              </label>
            </div>
            {p.overlap && (
              <div className="rounded-sm border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                <b>{p.overlap.days} Tag(e)</b> überlappen mit <b>{p.overlap.with}</b>.
                Netto zählt nur der nicht-überlappende Zeitraum.
              </div>
            )}
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
              Begründung
            </label>
            <textarea
              defaultValue={p.note}
              rows={4}
              className="mt-1 w-full rounded-sm border border-ink-200 bg-white px-2 py-1.5 text-xs text-ink-700"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------
// LEGACY-ZEILE (Ist-Zustand aus Screenshot)
// -------------------------------------------------------------
const LegacyRow = ({ p }) => {
  const isAnnahme = p.kind === "ANNAHME";
  return (
    <div
      className={cn(
        "rounded-sm border p-4",
        isAnnahme ? "border-amber-300 bg-amber-50/40" : "border-emerald-200 bg-white"
      )}
    >
      <div className="grid grid-cols-12 items-center gap-3">
        <input
          type="text"
          defaultValue={p.from}
          className="col-span-2 rounded-sm border border-ink-200 px-2 py-1.5 text-sm"
        />
        <span className="col-span-0 text-ink-400">—</span>
        <input
          type="text"
          defaultValue={p.to}
          className="col-span-2 rounded-sm border border-ink-200 px-2 py-1.5 text-sm"
        />
        <div className="col-span-2 text-sm font-semibold text-brand-700">
          {p.duration}
          {p.durationGross && (
            <div className="text-[10px] font-normal text-ink-500">gesamt {p.durationGross}</div>
          )}
        </div>
        <div className="col-span-1 flex items-center gap-1">
          <input
            type="number"
            defaultValue={p.faktor}
            className="w-14 rounded-sm border border-ink-200 px-1.5 py-1 text-sm"
          />
          <span className="text-sm text-ink-500">%</span>
        </div>
        <input
          type="text"
          defaultValue={p.employer}
          className="col-span-2 rounded-sm border border-ink-200 px-2 py-1.5 text-sm"
        />
        <input
          type="text"
          defaultValue={p.role}
          className="col-span-2 rounded-sm border border-ink-200 px-2 py-1.5 text-sm"
        />
        <div className="col-span-1 flex items-center gap-1">
          <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800">
            <CheckCircle2 className="h-3.5 w-3.5" /> ANR.
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span
          className={cn(
            "inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            isAnnahme
              ? "border-amber-300 bg-amber-100 text-amber-800"
              : "border-emerald-300 bg-emerald-100 text-emerald-800"
          )}
        >
          {isAnnahme ? "Annahme" : "KV-Regel"}
        </span>
      </div>
      <div className="mt-3 flex items-start gap-2 rounded-sm border border-ink-200 bg-white p-2 text-xs">
        <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-ink-300 accent-brand-500" />
        <span>
          <b className="uppercase tracking-wide">Selber Arbeitgeber</b> — Falls dieselbe Anstellung
          beim NEUEN Arbeitgeber vorlag, hier markieren. Position zählt dann zusätzlich zur
          Höchstanrechnungsdauer (KV-Sonderregel).
        </span>
      </div>
      {isAnnahme && (
        <div className="mt-2 flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 p-2 text-xs text-sky-800">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <b>Anrechnungsfaktor zu 100 % angenommen</b> — kein eindeutiger KV-Regel-Match. Bitte
            prüfen, ob die Tätigkeit als Angestellter oder Arbeiter eingestuft werden soll (z.B.
            Tätigkeitstext ergänzen, damit die KV-Regel greift).
          </span>
        </div>
      )}
      <div className="mt-3">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
          Begründung zur Anrechenbarkeit
        </div>
        <textarea
          defaultValue={
            isAnnahme
              ? "Hinweis: Aus dem Lebenslauf-Kontext abgeleitet — bitte Anstellungstyp prüfen (kann geringfügige oder reguläre Angestellten-Position sein). SV-Auszug oder Dienstvertrag entscheidet endgültig. ET trifft hier keine Entscheidung; Anrechnung vorläufig als ANNAHME gesetzt."
              : p.note
          }
          rows={3}
          className="mt-1 w-full rounded-sm border border-ink-200 bg-white px-2 py-1.5 text-xs"
        />
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Main Page
// -------------------------------------------------------------
export default function MockupStep3Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCase = ["fall1", "fall2", "fall3"].includes(searchParams.get("fall"))
    ? searchParams.get("fall")
    : "fall1";
  const [caseKey, setCaseKeyState] = useState(initialCase);
  const setCaseKey = (k) => {
    setCaseKeyState(k);
    setSearchParams({ fall: k }, { replace: true });
  };
  const currentCase = CASES[caseKey];
  const CAP_JAHRE = currentCase.cap;

  const [mode, setMode] = useState("neu"); // "aktuell" | "neu"
  const [filter, setFilter] = useState("alle");
  const [density, setDensity] = useState("kompakt"); // "kompakt" | "detail"
  const [showTimeline, setShowTimeline] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [positions, setPositions] = useState(currentCase.positions);

  // Wenn Fall wechselt → neu laden
  React.useEffect(() => {
    setPositions(CASES[caseKey].positions);
    setSelected(new Set());
    setFilter("alle");
    setExpandedId(null);
  }, [caseKey]);

  // Anrechenbar-Summe + per-row Cap-State (chronologisch kumulativ, in Tagen)
  // Faktor wird VOR dem Cap-Walk angewendet: effectiveDays = posDays * (faktor/100)
  const CAP_DAYS = CAP_JAHRE * 360;
  const positionsWithCap = useMemo(() => {
    const idIndex = new Map(positions.map((p, i) => [p.id, i]));
    const sorted = [...positions].sort((a, b) => parseDMY(a.from) - parseDMY(b.from));
    let cumulative = 0;
    const withCap = sorted.map((p) => {
      const posDays = daysBetween(p.from, p.to);
      if (!p.included) {
        return { ...p, capState: null, cappedDays: 0, effectiveDays: 0, posDays };
      }
      const faktor = typeof p.faktor === "number" ? p.faktor : 100;
      const effectiveDays = Math.round(posDays * (faktor / 100));
      let capState;
      let cappedDays;
      if (cumulative >= CAP_DAYS) {
        capState = "overflow";
        cappedDays = 0;
      } else if (cumulative + effectiveDays > CAP_DAYS) {
        capState = "partial";
        cappedDays = CAP_DAYS - cumulative;
      } else {
        capState = "within";
        cappedDays = effectiveDays;
      }
      cumulative += effectiveDays;
      return { ...p, capState, cappedDays, effectiveDays, posDays };
    });
    return withCap.sort((a, b) => idIndex.get(a.id) - idIndex.get(b.id));
  }, [positions, CAP_DAYS]);

  const anrechenbarDays = useMemo(
    () =>
      Math.min(
        positionsWithCap.reduce((acc, p) => acc + (p.cappedDays || 0), 0),
        CAP_DAYS
      ),
    [positionsWithCap, CAP_DAYS]
  );

  const bruttoDays = useMemo(
    () =>
      positions
        .filter((p) => p.included)
        .reduce((acc, p) => acc + daysBetween(p.from, p.to), 0),
    [positions]
  );

  const anrechenbarYMD = daysToYMD(anrechenbarDays);
  const bruttoYMD = daysToYMD(bruttoDays);
  const capReached = anrechenbarDays >= CAP_DAYS;
  const capReachedDate = useMemo(
    () => (capReached ? computeCapReachedDate(positions, CAP_JAHRE) : null),
    [positions, CAP_JAHRE, capReached]
  );
  const capProgressPct = Math.min(100, (anrechenbarDays / CAP_DAYS) * 100);


  const counts = useMemo(() => {
    const alle = positions.length;
    const anrechenbar = positions.filter((p) => p.included).length;
    const nicht = positions.filter((p) => !p.included).length;
    const prüfen = positions.filter((p) => p.kind === "ANNAHME").length;
    return { alle, anrechenbar, nicht, prüfen };
  }, [positions]);

  const visible = useMemo(() => {
    if (filter === "anrechenbar") return positionsWithCap.filter((p) => p.included);
    if (filter === "nicht") return positionsWithCap.filter((p) => !p.included);
    if (filter === "prüfen") return positionsWithCap.filter((p) => p.kind === "ANNAHME");
    return positionsWithCap;
  }, [positionsWithCap, filter]);

  const toggleSelect = (id, on) => {
    setSelected((old) => {
      const next = new Set(old);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const bulkMarkNichtAnr = () => {
    setPositions((old) =>
      old.map((p) => (selected.has(p.id) ? { ...p, included: false } : p))
    );
    setSelected(new Set());
  };

  const bulkMarkGeprueft = () => {
    setPositions((old) =>
      old.map((p) =>
        selected.has(p.id) && p.kind === "ANNAHME" ? { ...p, kind: "KV-REGEL" } : p
      )
    );
    setSelected(new Set());
  };

  const toggleIncluded = (id, v) => {
    setPositions((old) =>
      old.map((p) => (p.id === id ? { ...p, included: v, faktor: v ? p.faktor || 100 : 0 } : p))
    );
  };

  const setFaktor = (id, pct) => {
    const clamped = Math.max(0, Math.min(100, Math.round(pct)));
    setPositions((old) =>
      old.map((p) =>
        p.id === id
          ? { ...p, faktor: clamped, _factor_manual: clamped !== 100 && clamped !== 0 }
          : p
      )
    );
  };

  const resetDemo = () => {
    setPositions(CASES[caseKey].positions);
    setSelected(new Set());
    setFilter("alle");
    setExpandedId(null);
  };

  return (
    <div className="min-h-screen bg-ink-50/40">
      {/* Preview-Banner */}
      <div className="border-b border-brand-300 bg-brand-50 px-4 py-2 text-center text-xs text-brand-900">
        <b>PREVIEW-MOCKUP</b> — Diese Seite verwendet nur statische Beispieldaten. Keine
        Backend-Calls, keine LLM-Kosten. Nichts wird gespeichert.
        <span className="mx-2">·</span>
        <Link to="/" className="underline underline-offset-2 hover:text-brand-700">
          zurück zur Startseite
        </Link>
      </div>

      {/* Header */}
      <header className="border-b border-ink-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-ink-500">
              UI-Mockup · Schritt 3 · {currentCase.shortLabel}
            </div>
            <h1 className="font-cabinet text-2xl font-black tracking-tight text-ink-900">
              Vordienstzeiten — Vorschlag Kompakt-Ansicht
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-ink-600">
              {currentCase.kvName} · Ziel-VG: <b>{currentCase.zielVG}</b> · Stichtag:{" "}
              <b>{currentCase.stichtag}</b> · Cap: <b>{currentCase.cap} J.</b>
            </p>
          </div>

          <div className="flex flex-col items-end gap-2">
            {/* Fall-Selector */}
            <div className="inline-flex overflow-hidden rounded-sm border border-ink-300 bg-white text-xs">
              {Object.values(CASES).map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCaseKey(c.key)}
                  className={cn(
                    "border-r border-ink-200 px-3 py-1.5 font-medium last:border-r-0",
                    caseKey === c.key
                      ? "bg-brand-400 text-ink-900"
                      : "bg-white text-ink-600 hover:bg-ink-100"
                  )}
                  title={c.label}
                >
                  <span className="font-mono">{c.shortLabel}</span>
                  <span className="ml-1.5 rounded-sm bg-white/40 px-1 py-0.5 text-[10px] font-normal">
                    {c.positions.length} Zeilen
                  </span>
                </button>
              ))}
            </div>

            {/* Mode-Toggle */}
            <div className="inline-flex rounded-sm border border-ink-300 bg-white p-0.5 text-xs">
              <button
                onClick={() => setMode("aktuell")}
                className={cn(
                  "rounded-[3px] px-3 py-1.5 font-medium",
                  mode === "aktuell"
                    ? "bg-ink-900 text-white"
                    : "text-ink-600 hover:bg-ink-100"
                )}
              >
                Aktuell (Ist)
              </button>
              <button
                onClick={() => setMode("neu")}
                className={cn(
                  "rounded-[3px] px-3 py-1.5 font-medium",
                  mode === "neu" ? "bg-ink-900 text-white" : "text-ink-600 hover:bg-ink-100"
                )}
              >
                Vorschlag (Neu)
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-6">
        {mode === "aktuell" ? (
          // -----------------------------------------------------
          // IST-ZUSTAND
          // -----------------------------------------------------
          <div className="space-y-4">
            {/* Warning-Trio wie im Screenshot */}
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-4">
              <div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4" /> {currentCase.warnings.title}
              </div>
              <p className="text-sm text-amber-900">
                Dieser KV verlangt eine Prüfung, ob die unten markierten <b>Positionen</b> fachlich
                gleichwertig zur neuen Tätigkeit sind. Bitte jede gelb hinterlegte Zeile einzeln
                prüfen — falls <b>nicht gleichwertig</b>, das Häkchen bei &bdquo;ANR.&ldquo; entfernen.
              </p>
            </div>

            <ul className="space-y-2">
              {currentCase.warnings.bullets.map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900"
                >
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {t}
                </li>
              ))}
            </ul>

            {/* Filter-Chips */}
            <div className="flex flex-wrap items-center gap-2">
              {["alle", "anrechenbar", "nicht", "prüfen"].map((k) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium",
                    filter === k
                      ? "border-brand-400 bg-brand-50 text-brand-800"
                      : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
                  )}
                >
                  {k === "alle"
                    ? `Alle ${counts.alle}`
                    : k === "anrechenbar"
                    ? `Anrechenbar ${counts.anrechenbar}`
                    : k === "nicht"
                    ? `Nicht anrechenbar ${counts.nicht}`
                    : `Prüfen ${counts.prüfen}`}
                </button>
              ))}
            </div>

            {/* Zeilen */}
            <div className="space-y-3">
              {visible.map((p) => (
                <LegacyRow key={p.id} p={p} />
              ))}
            </div>
          </div>
        ) : (
          // -----------------------------------------------------
          // NEUER VORSCHLAG
          // -----------------------------------------------------
          <div className="space-y-4">
            {/* Konsolidierter Alert (D4 + D5) */}
            <div className="rounded-sm border-l-4 border-amber-400 border-y border-r border-y-amber-200 border-r-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="font-semibold text-amber-900">
                      {currentCase.warnings.title}
                      <AuditTag ids="D4·D5" />
                    </h2>
                    <span className="rounded-sm border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-medium text-amber-800">
                      {counts.prüfen} offen
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-amber-900">{currentCase.warnings.summary}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <button className="rounded-sm border border-amber-400 bg-white px-2.5 py-1 font-medium text-amber-800 hover:bg-amber-100">
                      Alle als geprüft markieren
                    </button>
                    <button className="rounded-sm border border-amber-400 bg-white px-2.5 py-1 font-medium text-amber-800 hover:bg-amber-100">
                      Alle Bezugszeiten ausschließen
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI-Panel (D6) — prominente Gesamtsumme + Höchstanrechnungsdauer-Info.
                Nicht sticky, um Layout-Konflikte zu vermeiden. */}
            <div className="rounded-sm border border-ink-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-start gap-x-8 gap-y-4">
                {/* Prominente Gesamt-Anrechenbarkeit — kompaktes Einzeilen-Format */}
                <div className="flex items-start gap-3">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                      Anrechenbar gesamt
                    </span>
                    <div className="mt-0.5 font-cabinet text-3xl font-black leading-none text-ink-900">
                      {fmtYMDShort(anrechenbarDays)}
                    </div>
                    <div className="mt-1 text-[11px] leading-snug text-ink-500">
                      brutto {fmtYMDShort(bruttoDays)} · {counts.anrechenbar} anrechenbare
                      Positionen
                    </div>
                  </div>
                  <AuditTag ids="D6" />
                </div>

                <div className="hidden h-14 w-px bg-ink-200 md:block" />

                {/* Höchstanrechnungsdauer + Progressbar */}
                <div className="flex min-w-[260px] max-w-md flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-ink-500">
                      Höchstanrechnungsdauer
                    </span>
                    <span className="text-sm font-semibold text-ink-800">
                      {CAP_JAHRE} Jahre lt. KV
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                    <div
                      className={cn(
                        "h-full transition-all",
                        capReached ? "bg-sky-600" : "bg-emerald-500"
                      )}
                      style={{ width: `${capProgressPct}%` }}
                    />
                  </div>
                  {capReached ? (
                    <div className="mt-0.5 flex items-start gap-1.5 text-[11px] leading-snug text-sky-800">
                      <Info className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>
                        Erreicht am <b>{capReachedDate}</b>. Spätere Beschäftigungen werden
                        dokumentiert, aber nicht mehr eingerechnet — siehe Zeitleiste.
                      </span>
                    </div>
                  ) : (
                    <div className="mt-0.5 text-[11px] text-ink-500">
                      Anrechnungsvolumen zu {Math.round(capProgressPct)} % ausgeschöpft
                    </div>
                  )}
                </div>

                <div className="ml-auto flex flex-wrap items-center gap-2 self-center">
                  <button
                    onClick={() => setShowTimeline((s) => !s)}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-ink-200 bg-white px-2.5 py-1.5 text-xs text-ink-700 hover:bg-ink-100"
                  >
                    <LineChart className="h-3.5 w-3.5" />
                    Zeitleiste {showTimeline ? "ausblenden" : "einblenden"}
                  </button>
                  <div className="inline-flex overflow-hidden rounded-sm border border-ink-200">
                    <button
                      onClick={() => setDensity("kompakt")}
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1.5 text-xs",
                        density === "kompakt"
                          ? "bg-ink-900 text-white"
                          : "bg-white text-ink-600 hover:bg-ink-100"
                      )}
                      title="Kompakte Zeilen (D2)"
                    >
                      <Layers className="h-3 w-3" /> Kompakt
                    </button>
                    <button
                      onClick={() => setDensity("detail")}
                      className={cn(
                        "flex items-center gap-1 px-2.5 py-1.5 text-xs",
                        density === "detail"
                          ? "bg-ink-900 text-white"
                          : "bg-white text-ink-600 hover:bg-ink-100"
                      )}
                    >
                      <Filter className="h-3 w-3" /> Detail
                    </button>
                  </div>
                  <button
                    onClick={resetDemo}
                    className="text-[11px] text-ink-500 underline underline-offset-2 hover:text-ink-800"
                  >
                    Demo zurücksetzen
                  </button>
                </div>
              </div>
            </div>

            {/* Timeline (D11) */}
            {showTimeline && (
              <Timeline positions={positionsWithCap} capReachedDate={capReachedDate} />
            )}

            {/* Filter-Chips (D8) */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                Filter
              </span>
              <button
                onClick={() => setFilter("alle")}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium",
                  filter === "alle"
                    ? "border-ink-900 bg-ink-900 text-white"
                    : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
                )}
              >
                Alle · {counts.alle}
              </button>
              <button
                onClick={() => setFilter("anrechenbar")}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium",
                  filter === "anrechenbar"
                    ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                    : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
                )}
              >
                <CheckCircle2 className="mr-1 inline h-3 w-3" /> Anrechenbar · {counts.anrechenbar}
              </button>
              <button
                onClick={() => setFilter("nicht")}
                disabled={counts.nicht === 0}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium disabled:opacity-40",
                  filter === "nicht"
                    ? "border-ink-400 bg-ink-100 text-ink-800"
                    : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
                )}
              >
                <XCircle className="mr-1 inline h-3 w-3" /> Nicht anrechenbar · {counts.nicht}
              </button>
              <button
                onClick={() => setFilter("prüfen")}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-semibold",
                  filter === "prüfen"
                    ? "border-amber-500 bg-amber-500 text-white"
                    : counts.prüfen > 0
                    ? "border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    : "border-ink-200 bg-white text-ink-600 hover:bg-ink-100"
                )}
              >
                <FileWarning className="mr-1 inline h-3 w-3" /> Prüfen · {counts.prüfen} offen
              </button>
            </div>

            {/* Bulk-Actions (D7) */}
            {selected.size > 0 && (
              <div className="sticky top-16 z-10 flex items-center gap-3 rounded-sm border border-ink-900 bg-ink-900 px-4 py-2 text-sm text-white shadow-lg">
                <span className="font-semibold">{selected.size} ausgewählt</span>
                <div className="h-4 w-px bg-white/30" />
                <button
                  onClick={bulkMarkGeprueft}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-white/10 px-2.5 py-1 text-xs font-medium hover:bg-white/20"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Als geprüft markieren
                </button>
                <button
                  onClick={bulkMarkNichtAnr}
                  className="inline-flex items-center gap-1.5 rounded-sm bg-white/10 px-2.5 py-1 text-xs font-medium hover:bg-white/20"
                >
                  <XCircle className="h-3.5 w-3.5" /> ANR entfernen
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  className="ml-auto text-xs text-white/70 hover:text-white"
                >
                  Auswahl aufheben
                </button>
                <AuditTag ids="D7" />
              </div>
            )}

            {/* Kompakt-Zeilen */}
            <div className="space-y-2">
              {visible.map((p) => (
                <CompactRow
                  key={p.id}
                  p={p}
                  expanded={density === "detail" || expandedId === p.id}
                  onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  checked={selected.has(p.id)}
                  onCheck={(on) => toggleSelect(p.id, on)}
                  included={p.included}
                  onIncluded={(v) => toggleIncluded(p.id, v)}
                  onFaktor={(pct) => setFaktor(p.id, pct)}
                />
              ))}
            </div>

            {/* Legende / Findings-Referenz */}
            <div className="mt-8 rounded-sm border border-ink-200 bg-white p-4">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-700">
                Umgesetzte Audit-Findings
              </h3>
              <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-ink-700 md:grid-cols-2">
                {[
                  ["D1", "Redundanz-Kaskade (Badge / Info-Box / Begründung) auf 1 Badge + Popover reduziert; Farbrand pro Zeile entfernt (redundant zum Badge)"],
                  ["D2", "Zeilenhöhe von ~200 px → ~40 px im Kompakt-Modus (Expand-Drawer)"],
                  ["D3", "Selber-AG als Chip / Icon statt volle Erklärungszeile in jeder Zeile"],
                  ["D4", "Warning-Trio zu einem konsolidierten Alert zusammengefasst"],
                  ["D5", "Warning-Hierarchie klar getrennt (nur ein prominenter Alert-Streifen)"],
                  ["D6", "Sticky-KPI mit prominenter Gesamtsumme + Fortschrittsbalken; Höchstanrechnungsdauer-Erreichen als ruhige blaue Info (nicht rot)"],
                  ["D7", "Bulk-Aktionen (Als geprüft, ANR entfernen) mit Multi-Select"],
                  ["D8", "Prüfen-Chip prominenter (orange gefüllt), Nicht-anrechenbar-Chip disabled"],
                  ["D9", "ANR-Toggle als Switch mit klarem an/aus-Label statt Doppelrolle-Badge"],
                  ["D10", "Zeitliche Überlappungen werden im Zeitstrahl gestapelt visualisiert (Lane-Layout) statt in der Zeile markiert"],
                  ["D11", "Optionale Zeitleiste; blaue Grenz-Linie markiert Erreichen der Höchstanrechnungsdauer, spätere Positionen gedämpft"],
                  ["D12", "Textarea auto-resize-fähig im Detail-Drawer"],
                  ["D13", "Empty-State-Handling durch disabled Chips bei Count 0"],
                  ["D14", "Filter-Chips als sortier-/filterfähige Row (Basis)"],
                  ["D15", "Löschen-Icon mit Hover-Rot-Feedback (Confirm-Dialog wäre nächster Schritt)"],
                  ["D16", "Sidebar-KV-Karte bleibt anklickbar & Kontext-relevant"],
                ].map(([id, text]) => (
                  <div key={id} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-4 min-w-8 items-center justify-center rounded-sm border border-brand-300 bg-brand-50 px-1 text-[10px] font-mono font-semibold text-brand-700">
                      {id}
                    </span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <footer className="mt-10 flex items-center justify-between border-t border-ink-200 pt-4 text-xs text-ink-500">
          <span>
            Preview-Mockup · statische Daten · keine Persistenz. Vollständiger Audit unter{" "}
            <code className="rounded bg-ink-100 px-1 py-0.5">/app/memory/UI_UX_AUDIT_2026-02.md</code>
          </span>
          <Link to="/" className="inline-flex items-center gap-1 hover:text-ink-800">
            zurück zur Startseite <ArrowRight className="h-3 w-3" />
          </Link>
        </footer>
      </main>
    </div>
  );
}
